import { Box, Button, Stack, Typography } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import { MediumCustomIcon, SmallCustomIcon } from "../../layer00";
import { StyledTextField } from "../../layer01";

type BoBrainMemoryBlockProps = {
  // Heading shown in read mode.
  title: string;
  // Initial value of the title field in edit mode; defaults to `title`.
  editTitle?: string;
  body: string;
  // Initial value of the details field in edit mode; defaults to `body`.
  editBody?: string;
  // Rejects on failure so the editor stays open with the draft.
  onSave?: (title: string, body: string) => Promise<void>;
  onResume?: () => void;
  resumeButtonText?: string;
};

const collapsedLineCount = 3;
// Matches the varchar(255) title column on the API.
const titleMaxLength = 255;

const linkButtonSx = {
  minWidth: "auto",
  flexShrink: 0,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: ".5rem",
  fontSize: ".875rem",
  fontWeight: 500,
  lineHeight: "1.5rem",
  color: "#2E408A",
  "&:hover": {
    background: "none",
  },
} as const;

// The trailing button (Edit or View) gets a fixed width with left-aligned
// content so its icon and label line up across blocks whatever its label.
const lastLinkButtonSx = {
  ...linkButtonSx,
  // Just wide enough for the icon + "View" (the widest trailing label).
  minWidth: "3.75rem",
  justifyContent: "flex-start",
} as const;

const editFieldSx = {
  padding: ".5rem",
  background: "#FFFFFF",
  border: "1px solid #CCD2DC",
  borderRadius: ".5rem",
  lineHeight: "1.25rem",
} as const;

export const BoBrainMemoryBlock = ({
  onResume,
  onSave,
  body,
  title,
  editBody,
  editTitle,
  resumeButtonText = "Resume",
}: BoBrainMemoryBlockProps) => {
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [draft, setDraft] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const node = bodyRef.current;
    if (!node || isExpanded) {
      return;
    }

    setIsTruncated(node.scrollHeight > node.clientHeight);
  }, [body, isExpanded, isEditing]);

  const onApply = async () => {
    if (!onSave) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(titleDraft.trim(), draft.trim());
      setIsEditing(false);
    } catch {
      // Keep the editor open so the draft isn't lost.
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <Stack direction="column" rowGap=".5rem">
        <StyledTextField
          label="Title"
          value={titleDraft}
          onChange={(event) =>
            setTitleDraft(event.target.value.slice(0, titleMaxLength))
          }
          sx={editFieldSx}
        />
        <StyledTextField
          label="Details"
          multiline
          minRows={4}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          sx={editFieldSx}
        />
        <Stack
          direction="row"
          columnGap=".5rem"
          rowGap=".5rem"
          justifyContent="flex-end"
          flexWrap="wrap"
        >
          <Button
            variant="outlined"
            disabled={isSaving}
            onClick={() => setIsEditing(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isSaving || !draft.trim()}
            onClick={onApply}
          >
            {isSaving ? "Saving…" : "Apply"}
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      direction="column"
      rowGap=".5rem"
      padding={{
        xs: ".75rem",
        sm: "1rem",
      }}
      borderRadius=".5rem"
      bgcolor="#F7F7F7"
    >
      <Stack
        direction="row"
        columnGap=".5rem"
        rowGap=".25rem"
        alignItems="center"
        flexWrap="wrap"
      >
        <Typography
          variant="cardSubtitle"
          flex="1 1 0"
          minWidth={0}
          color="#001F4E"
        >
          {title}
        </Typography>
        {onResume && (
          <Button
            variant="text"
            sx={onSave ? linkButtonSx : lastLinkButtonSx}
            onClick={onResume}
            startIcon={<SmallCustomIcon icon="MESSAGE" stroke="#425CC7" />}
          >
            {resumeButtonText}
          </Button>
        )}
        {onSave && (
          <Button
            variant="text"
            sx={lastLinkButtonSx}
            onClick={() => {
              setTitleDraft(editTitle ?? title);
              setDraft(editBody ?? body);
              setIsEditing(true);
            }}
            startIcon={<SmallCustomIcon icon="EDIT" stroke="#425CC7" />}
          >
            Edit
          </Button>
        )}
      </Stack>
      <Typography
        ref={bodyRef}
        variant="cardBodyRegular"
        lineHeight="1.25rem"
        whiteSpace="pre-line"
        color="#53565A"
        sx={
          isExpanded
            ? undefined
            : {
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: collapsedLineCount,
                overflow: "hidden",
              }
        }
      >
        {body}
      </Typography>
      {isTruncated && (
        <Box>
          <Button
            variant="text"
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={linkButtonSx}
            startIcon={
              <MediumCustomIcon
                icon={isExpanded ? "CHEVRONUP" : "CHEVRONDOWN"}
                stroke="#2E408A"
                width={16}
                height={16}
              />
            }
          >
            {isExpanded ? "Read less" : "Read more"}
          </Button>
        </Box>
      )}
    </Stack>
  );
};
